import * as React from "react";

/**
 * The confirmation context and its two hooks. `ConfirmProvider` is next door in
 * `Confirm.tsx`.
 *
 * ── Why this is a file of its own ───────────────────────────────────────────
 *
 * It used to sit in `Confirm.tsx` beside the provider, and that file therefore
 * exported a component, two hooks and a module-level `createContext` together.
 * React Fast Refresh only treats a module as a refresh boundary when everything
 * it exports is a component; a mixed module gets re-evaluated instead, which
 * **creates a brand new context object**. Consumers that were not re-rendered in
 * the same pass keep reading the previous one, find no provider above them, and
 * throw `useConfirm must be used inside <ConfirmProvider>` on a tree where the
 * provider is plainly mounted. A full page reload clears it, which is exactly
 * what makes it look like a phantom.
 *
 * Splitting the context out means the file holding it never changes when the
 * dialog's markup does, so the identity survives the edit.
 *
 * ── The API ────────────────────────────────────────────────────────────────
 *
 * Confirmation as a promise: `if (await confirm({...})) …`.
 *
 * A provider and a hook rather than a component every page mounts, because the
 * alternative is a `confirming` state variable, a `pendingAction` ref and a
 * `<ConfirmDialog>` in the JSX of every screen that can delete something. That
 * is four things to keep in step per call site, and the one that is always
 * missed is the second call site on the same page.
 *
 * The answer is deliberately a boolean and never a callback. `await` puts the
 * decision inline in the handler that asked, so the code reads in the order it
 * happens and there is nowhere for a stale closure to live.
 *
 * ── What this is not for ───────────────────────────────────────────────────
 *
 * Not for anything reversible. A confirmation on an action the user can simply
 * undo trains them to click through confirmations, which is precisely how the
 * one that mattered gets clicked through too. Deactivating someone asks;
 * reactivating them does not.
 */
export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button. For anything that removes access or destroys data. */
  danger?: boolean;
}

export type Confirm = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = React.createContext<Confirm | null>(null);

export function useConfirm(): Confirm {
  const confirm = React.useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return confirm;
}

/**
 * Guard a form's close against losing unsaved work.
 *
 * **Route every way out through the returned callback** - Cancel, the close
 * button, Escape and the backdrop - or the guard covers whichever one you
 * remembered and silently misses the rest. `Drawer` and `Modal` already funnel
 * all four into a single `onClose`, so passing this as that prop covers them.
 *
 * It does nothing at all while `busy`. A save in flight has already left the
 * browser, and closing the form over it abandons a request that is going to
 * succeed, leaving the list showing something the database no longer says.
 */
export function useDiscardGuard(dirty: boolean, busy: boolean, onClose: () => void) {
  const confirm = useConfirm();

  return React.useCallback(async () => {
    if (busy) return;
    if (!dirty) {
      onClose();
      return;
    }

    const discard = await confirm({
      title: "Discard changes?",
      message: "Your unsaved changes will be lost.",
      confirmLabel: "Discard",
      cancelLabel: "Keep editing",
      danger: true,
    });

    if (discard) onClose();
  }, [confirm, dirty, busy, onClose]);
}
